/**
 * excel导出工具类
 * @author 赵美丹
 */

export const openDownloadDialog = (url, saveName) => {
  let realUrl = url;
  if (typeof url === "object" && url instanceof Blob) {
    realUrl = URL.createObjectURL(url); // 创建blob地址
  }
  const aLink = document.createElement("a");
  aLink.href = realUrl;
  aLink.download = `${saveName}.xls` || ""; // HTML5新增的属性，指定保存文件名，可以不要后缀，注意，file:///模式下不会生效
  let event;
  if (window.MouseEvent) event = new MouseEvent("click");
  else {
    event = document.createEvent("MouseEvents");
    event.initMouseEvent(
      "click",
      true,
      false,
      window,
      0,
      0,
      0,
      0,
      0,
      false,
      false,
      false,
      false,
      0,
      null
    );
  }
  aLink.dispatchEvent(event);
};

export const sheet2blob = async (sheet, sheetName = "列表") => {
  const workbook = {
    SheetNames: [sheetName],
    Sheets: {},
  };
  workbook.Sheets[sheetName] = sheet; // 生成excel的配置项

  const xlsx = (
    await import(/* webpackChunkName: "xlsx-style-tw" */ "xlsx-style-tw")
  ).default;
  const wopts = {
    bookType: "xlsx", // 要生成的文件类型
    bookSST: true, // 是否生成Shared String Table，官方解释是，如果开启生成速度会下降，但在低版本IOS设备上有更好的兼容性
    type: "binary",
    cellStyles: true,
  };
  const wbout = xlsx.write(workbook, wopts);
  // 字符串转ArrayBuffer
  function s2ab(s) {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    // eslint-disable-next-line no-bitwise
    for (let i = 0; i !== s.length; i += 1) view[i] = s.charCodeAt(i) & 0xff;
    return buf;
  }
  return new Blob([s2ab(wbout)], {
    type: "application/octet-stream",
  });
};

// row从1开始，col从0开始，暂不变动了
export const getCellPosition = (rowIndex, colIndex) => {
  if (colIndex <= 25) {
    return String.fromCharCode(65 + colIndex) + (rowIndex + 1);
  }

  let index = colIndex;
  let position = "";
  let relative = 0;
  while (index > 0) {
    relative = (index % 26) + 1;
    position = String.fromCharCode(relative + 64) + position;
    index = (index - relative) / 26;
  }
  return position + (rowIndex + 1);
};

function getJsxContent(jsxElment) {
  if (typeof jsxElment === "string") return jsxElment;

  if (!jsxElment || !jsxElment.props) return "";

  const { children, content, type } = jsxElment.props;

  // 标签组件忽略
  if (type === "fill") return "";

  if (children) {
    if (typeof children === "string") {
      return children;
    }
    if (Array.isArray(children)) {
      return children.reduce(
        (allContent, child) => `${allContent}${getJsxContent(child)}`,
        ""
      );
    }
    return getJsxContent(children);
  }

  if (content) return content;

  return "";
}

const getNodeContent = (el) => {
  if (typeof el === "string") return el;
  if (!el) return "";
  if (el.childNodes?.length === 0) {
    if (el.textContent) return el.textContent;
    // input等
    if (el.value) return el.value;
    return "";
  }
  return getNodeContent(el.childNodes[0]);
};
export const borderStyle = {
  // 设置边框
  top: { style: "thin" },
  bottom: { style: "thin" },
  left: { style: "thin" },
  right: { style: "thin" },
};

const titleStyle = {
  font: {
    name: "宋体",
    sz: 18,
    bold: true,
  },
  alignment: {
    vertical: "center",
    horizontal: "center",
    wrapText: false,
  },
  border: borderStyle,
};

const headerStyle = {
  font: {
    name: "宋体",
    sz: 14,
    bold: true,
  },
  alignment: {
    vertical: "center",
    horizontal: "center",
    wrapText: false,
  },
  border: borderStyle,
};

const contentStyle = {
  font: {
    name: "宋体",
    sz: 12,
  },
  alignment: {
    wrapText: true,
  },
  border: borderStyle,
};
/**
 * 导出方法
 * @param exportData 需要导出的数据 [{},{},{},{},{}]
 * @param exportHead 表头 ['','','','','']
 * @param saveName 保存的名称 '导出'
 * @param exportColumns 需要导出数据中的列配置
 */
export const exportFn = async (
  exportData = [],
  exportColumns = [],
  saveName = "导出"
) => {
  const sheetData = {};
  const headRow = 2; // 表头所占行数
  let columnCount = 0;
  let rowCount = 1;
  let position = "A1";
  const colsWidth = [];
  // 需要合并的单元格
  const merges = [];

  const getWpx = (value) => {
    const charLen = (value.match(/\w+/g) || []).join("").length;
    const wpx = (value.length - charLen) * 15 + charLen * 8;
    return Math.min(Math.max(wpx, 40), 800);
  };

  exportColumns = exportColumns.filter((col) => !col.unexport);
  // 获取深度和占用列数量
  const getDepth = (col, depth = 0) => {
    const nowDepth = depth + 1;
    let colCount = col.children?.length || 1;
    if (nowDepth > rowCount) rowCount = nowDepth;
    if (colCount > 1) {
      col.children = col.children.map((child) => {
        const result = getDepth(child, nowDepth);
        colCount = colCount - 1 + result.colCount;
        return result;
      });
    }
    return {
      ...col,
      depth: nowDepth,
      colCount,
    };
  };
  exportColumns = exportColumns.map((col) => {
    const result = getDepth(col);
    columnCount += result.colCount;
    return result;
  });
  // 标题行
  for (let i = 0; i < columnCount; i++) {
    position = getCellPosition(0, i);
    sheetData[position] = {
      v: saveName,
      s: titleStyle,
    };
  }
  merges.push({
    // 第一行（标题）
    s: {
      // s为开始
      c: 0, // 开始列
      r: 0, // 开始取值范围
    },
    e: {
      // e结束
      c: columnCount - 1, // 结束列
      r: 0, // 结束范围
    },
  });
  const handleColumn = (column, preColCount) => {
    if (column.children?.length > 0) {
      let preCol = preColCount;
      column.children.forEach((col) => {
        handleColumn(col, preCol);
        preCol += col.colCount;
      });
    }
    const startCol = preColCount;
    const startRow = headRow + column.depth - 2;
    const endCol = preColCount + column.colCount - 1;
    const endRow =
      column.depth !== rowCount && !column.children?.length
        ? headRow + rowCount - 2
        : headRow + column.depth - 2;
    merges.push({
      s: {
        // s为开始
        c: startCol, // 开始列
        r: startRow, // 开始行
      },
      e: {
        // e结束
        c: endCol, // 结束列
        // 非最后一层且没有children的需要合并到最后一层
        r: endRow, // 结束行
      },
    });
    // 其他被合并的单元格，也需要添加样式，不然会没有边框线
    for (let i = startCol; i <= endCol; i++) {
      for (let j = startRow; j <= endRow; j++) {
        // headRow: 从哪行开始
        // column.depth: 深度
        position = getCellPosition(j, i);
        const title = getJsxContent(column.title);
        colsWidth[preColCount] = {
          wpx: title.length > 2 ? Math.max(getWpx(title), 120) : 50,
        };
        sheetData[position] = {
          v: title,
          s: headerStyle,
        };
      }
    }
  };
  // 处理表头
  let preColCount = 0;
  exportColumns.forEach((col) => {
    handleColumn(col, preColCount);
    preColCount += col.colCount;
  });
  const handleData = (row, rowIndex, col, preCol) => {
    if (col.children?.length) {
      let pre = preCol;
      col.children.forEach((c) => {
        handleData(row, rowIndex, c, pre);
        pre += c.colCount;
      });
      return;
    }
    const { dataIndex } = col;
    let value = row[dataIndex];

    if (dataIndex === "index") {
      // 序号列
      value = rowIndex + 1;
    } else if (col.renderExport || col.render) {
      if (dataIndex.includes(".")) {
        try {
          // eslint-disable-next-line no-eval
          value = eval(`row.${dataIndex}`);
        } catch {
          value = row[dataIndex] || "";
        }
      }
      const jsxElement = col.renderExport
        ? col.renderExport(value, row)
        : col.render(value, row, rowIndex);
      value = getJsxContent(jsxElement);

      // 金额去掉千分位分隔
      if (col.align === "right") {
        value = value.replace(/,/g, "");
      }
    } else if (`${dataIndex}`.includes(".")) {
      /* eslint-disable no-eval */
      try {
        value = eval(`row.${dataIndex}`);
      } catch {
        value = row[dataIndex] || "";
      }
    }

    if (!value && value !== 0) {
      value = "";
    }
    value = `${value}`.replace(/<br\/>/g, "\r\n");
    colsWidth[preCol].wpx = Math.max(colsWidth[preCol].wpx, getWpx(value));
    position = getCellPosition(headRow + rowCount + rowIndex - 1, preCol);

    sheetData[position] = {
      v: value,
      s: contentStyle,
      t: col.align === "right" && !isNaN(value) ? "n" : "s",
      z: !isNaN(value) && `${value}`.includes(".") ? "0.00" : "0",
    };
  };
  exportData.forEach((row, rowIndex) => {
    let preCol = 0;
    exportColumns.forEach((col) => {
      handleData(row, rowIndex, col, preCol);
      preCol += col.colCount;
    });
  });
  sheetData["!ref"] = `A1:${getCellPosition(
    rowCount + exportData.length,
    columnCount
  )}`;
  sheetData["!cols"] = colsWidth;
  sheetData["!rows"] = [{ hpx: 40 }];
  sheetData["!merges"] = merges.filter((merge) => {
    return !(merge.s.c === merge.e.c && merge.s.r === merge.e.r);
  });
  const blob = await sheet2blob(sheetData);
  openDownloadDialog(blob, saveName);
};
/**
 * 导出table
 *! table头部必须有col用来表示列数
 */
export const exportTable = async (table, title, fileName) => {
  const sheetData = {};
  const data = {};
  // 需要总列数，用于标题行
  let colTotal = 0;
  let position = "A1";
  const colsWidth = [];
  const headRow = 1;
  const getMinMissing = (nums, max) => {
    let n = 1;
    while (nums.includes(`${n}`) || (max && n === max)) n++;
    return n;
  };
  const getMinCoordinate = () => {
    const rowKeys = Object.keys(data);
    if (rowKeys.length === 0) return [0, 0];
    let currentRow = 0;
    for (let i = 0; i < rowKeys.length; i++) {
      const rowKey = Number(rowKeys[i]);
      if (rowKey !== currentRow) return [currentRow, 0];
      currentRow += 1;
      const colKeys = Object.keys(data[rowKey]);
      const minCol = getMinMissing(colKeys, colTotal);
      if (minCol <= colTotal) return [rowKey, minCol];
    }
    return [rowKeys.length, 0];
  };
  sheetData["!merges"] = [];
  table.childNodes.forEach((node) => {
    if (node.tagName === "COL") {
      colTotal++;
      colsWidth.push({
        wpx: isNaN(node.width)
          ? node.width
          : Number(node.width.replace("px", "")),
      });
    }
    if (node.tagName === "TR") {
      node.childNodes.forEach((n) => {
        if (n.tagName === "TD") {
          const [rowKey, colKey] = getMinCoordinate();
          let v = n.textContent ? n.textContent : getNodeContent(n);
          v = v.replace(/,/g, "");
          let t = isNaN(Number(v)) ? "s" : "n";
          let z = `${v}`.includes(".") ? "0.00" : "0";
          if (v.includes("%")) {
            v = Number(v.substr(0, v.length - 1)) / 100;
            t = "n";
            z = "0.00%";
          }
          if (!data[rowKey]) data[rowKey] = {};
          data[rowKey][colKey] = {
            v,
            t,
            z,
          };
          if (n.rowSpan > 1) {
            for (let i = 1; i < n.rowSpan; i++) {
              if (!data[rowKey + i]) data[rowKey + i] = {};
              data[rowKey + i][colKey] = {
                v,
                t,
                z,
              };
            }
          }
          if (n.colSpan > 1) {
            for (let i = 1; i < n.colSpan; i++) {
              data[rowKey][colKey + i] = {
                v,
                t,
                z,
              };
            }
          }
          if (n.rowSpan > 1 || n.colSpan > 1) {
            sheetData["!merges"].push({
              s: {
                c: colKey,
                r: rowKey + headRow,
              },
              e: {
                c: colKey + n.colSpan - 1,
                r: rowKey + headRow + n.rowSpan - 1,
              },
            });
          }
        }
      });
    }
  });
  // 标题行
  for (let i = 0; i < colTotal; i++) {
    position = getCellPosition(0, i);
    sheetData[position] = {
      v: title,
      s: titleStyle,
      t: "s",
    };
  }
  // 标题行合并列
  sheetData["!merges"].unshift({
    // 第一行（标题）
    s: {
      // s为开始
      c: 0, // 开始列
      r: 0, // 开始取值范围
    },
    e: {
      // e结束
      c: colTotal - 1, // 结束列
      r: 0, // 结束范围
    },
  });
  const rowKeys = Object.keys(data);
  rowKeys.forEach((rowKey) => {
    rowKey = Number(rowKey);
    const colKeys = Object.keys(data[rowKey]);
    colKeys.forEach((colKey) => {
      colKey = Number(colKey);
      position = getCellPosition(rowKey + headRow, colKey);
      const { v, z, t } = data[rowKey][colKey];
      sheetData[position] = {
        v,
        s: contentStyle,
        t,
        z,
      };
    });
  });
  sheetData["!ref"] = `A1:${position}`;
  sheetData["!cols"] = colsWidth;
  sheetData["!rows"] = [{ hpx: 40 }];
  const blob = await sheet2blob(sheetData);
  openDownloadDialog(blob, fileName);
};
/**
 * 填充满所有单元格（用于所有单元格都要画线）
 * @param {*} sheetData
 * @param {*} leftTopCell: A1
 * @param {*} rightBottomCell: I54
 * @param {*} attrs
 */
export const fillSheetData = (
  sheetData,
  leftTopCell,
  rightBottomCell,
  attrs
) => {
  const cloneData = { ...sheetData };
  const leftTopChar = leftTopCell.substring(0, 1);
  const rightBottomChar = rightBottomCell.substring(0, 1);
  const leftTopRow = Number(leftTopCell.substring(1, leftTopCell.length));
  const rightBottomRow = Number(
    rightBottomCell.substring(1, rightBottomCell.length)
  );
  const leftTopCol = leftTopChar.charCodeAt() - 64;
  const rightBottomCol = rightBottomChar.charCodeAt() - 64;
  for (let r = leftTopRow; r <= rightBottomRow; r++) {
    for (let c = leftTopCol; c <= rightBottomCol; c++) {
      const cell = getCellPosition(r - 1, c - 1);
      if (!cloneData[cell]) {
        cloneData[cell] = attrs;
      }
    }
  }
  return cloneData;
};
